import CustomDataGrid from 'components/CustomDataGrid';
import { useEffect, useState, useMemo } from 'react';
import {
  CreditStatus,
  DisbursedCredit,
  PaymentOrderStatus,
  PaymentSortModel,
  PaymentType,
} from 'types';
import { useLazyQuery } from '@apollo/client';
import { columnsDisbursed } from '../Columns';
import { GET_CREDIT_BY_STATUS, GET_PAYMENT_ORDERS } from '../queries';
import { OPTIONS_ROWS_PER_PAGE, ORDER, ORDER_BY_CREATED_AT } from '..';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import { DateRange } from 'react-day-picker';
import { NetworkError } from 'components/CustomDataGrid/EmptyStates/NetworkError';
import { RequestError } from 'components/CustomDataGrid/EmptyStates/RequestError';
import { NoData } from 'components/CustomDataGrid/EmptyStates/NoData';
import { CustomToolbar } from './CustomToolbar';

interface Props {
  sortDateModel: string | null;
  dateRange: DateRange | undefined;
  inputFilter: string;
  totalItems?: number;
  paymentType: PaymentType;
  onChangeInputFilter: (value: string) => void;
  onChangePaymentType: (type: PaymentType) => void;
  changeTab: (tab: number) => void;
  handleFilterButton: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const Disbursed = ({
  inputFilter,
  dateRange,
  sortDateModel,
  totalItems,
  paymentType,
  onChangeInputFilter,
  onChangePaymentType,
  changeTab,
  handleFilterButton,
}: Props) => {
  const [allPayments, setAllPayments] = useState<DisbursedCredit[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(OPTIONS_ROWS_PER_PAGE[0]);
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const initialStateGrid: GridInitialStateCommunity = {
    pagination: {
      paginationModel: {
        page: page,
        pageSize: rowsPerPage,
      },
    },
  };
  const sortModel: GridSortModel = [
    {
      field: ORDER_BY_CREATED_AT,
      sort: ORDER,
    },
  ];

  const [
    fetchPaymentOrders,
    { loading: loadingPaymentOrders, error: errorPaymentOrders, refetch: refetchGetPaymentOrders },
  ] = useLazyQuery(GET_PAYMENT_ORDERS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      page: 0,
      limit: 10000,
      order: ORDER,
      orderBy: ORDER_BY_CREATED_AT,
      filters: {
        statuses: PaymentOrderStatus.APPLIED,
        search: '',
        datesFilter: {
          dates: {
            startDate: dateRange?.from?.toISOString(),
            endDate: dateRange?.to?.toISOString(),
          },
          filterField: sortDateModel,
        },
      },
    },
  });

  const [
    fetchSpeiCredits,
    { loading: loadingCredits, error: errorCredits, refetch: refetchGetCredits },
  ] = useLazyQuery(GET_CREDIT_BY_STATUS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      page: 0,
      limit: 10000,
      order: ORDER,
      orderBy: ORDER_BY_CREATED_AT,
      filters: {
        statuses: CreditStatus.ACTIVE,
        paymentType: PaymentType.SPEI,
        clientName: '',
        datesFilter: {
          dates: {
            startDate: dateRange?.from?.toISOString(),
            endDate: dateRange?.to?.toISOString(),
          },
          filterField: PaymentSortModel.CREATION,
        },
      },
    },
  });

  const handleRefetch = () => {
    refetchGetPaymentOrders();
    refetchGetCredits();
  };

  const handleChangePage = (objectPages: GridPaginationModel) => {
    setPage(objectPages.page);
    setRowsPerPage(objectPages.pageSize);
  };

  const handleErrorButton = () => {
    setIsOnlineState(navigator.onLine);
    if (navigator.onLine) {
      handleRefetch();
    }
  };

  useEffect(() => {
    setPage(0);
  }, [inputFilter]);

  useEffect(() => {
    const getData = async () => {
      try {
        const isSpei = paymentType === PaymentType.SPEI;
        const response = isSpei ? await fetchSpeiCredits() : await fetchPaymentOrders();
        if (response?.data) {
          const data = isSpei
            ? response.data.getCreditsByStatus
            : response.data.getPaymentOrders.paymentOrders;
          setAllPayments(data);
        } else {
          throw new Error('Error al obtener las órdenes de pago desembolsadas');
        }
      } catch (error) {
        console.log(error);
      }
    };
    getData();
  }, [paymentType, dateRange]);

  const filteredPayments = useMemo(() => {
    if (!inputFilter) return allPayments;
    return allPayments.filter((payment) => {
      const fullName =
        'clientFullName' in payment
          ? payment.clientFullName.toLowerCase()
          : payment.clientName.toLowerCase();
      const f = inputFilter.toLowerCase();
      return fullName.includes(f);
    });
  }, [allPayments, inputFilter]);

  const paginatedPayments = useMemo(
    () => filteredPayments.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredPayments, page, rowsPerPage],
  );

  const hasNoData =
    allPayments.length === 0 &&
    !inputFilter &&
    !dateRange &&
    !loadingPaymentOrders &&
    !loadingCredits;
  const noResultsWithFilter = inputFilter.length > 0 && filteredPayments.length === 0;

  if (!isOnlineState) return <NetworkError handleRetry={handleErrorButton} />;
  if (errorPaymentOrders && errorCredits) return <RequestError handleRetry={handleErrorButton} />;
  if (hasNoData) return <NoData message="No hay órdenes de pago desembolsadas" />;
  if (noResultsWithFilter)
    return (
      <NoData message="No se encontraron resultados para tu búsqueda. Recuerda que la búsqueda se realiza sobre todos los resultados." />
    );

  return (
    <CustomDataGrid
      title="Listado de créditos desembolsados"
      loading={loadingPaymentOrders || loadingCredits}
      data={paginatedPayments}
      dataColumns={columnsDisbursed(handleRefetch, changeTab)}
      initialState={initialStateGrid}
      pageSizeOptions={OPTIONS_ROWS_PER_PAGE}
      handleChangePage={handleChangePage}
      sort={sortModel}
      page={page}
      pageSize={rowsPerPage}
      totalRows={filteredPayments.length}
      paymentType={paymentType}
      filterTypeButtons={{
        disabled: loadingCredits || loadingPaymentOrders,
        onClick: (newPaymentType) => {
          onChangePaymentType(newPaymentType);
          setPage(0);
          onChangeInputFilter('');
        },
      }}
      filterButton={{ handleClick: handleFilterButton }}
      toolbar={CustomToolbar}
    />
  );
};

export default Disbursed;
